import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { fetchFamilyNotes, postFamilyNote, FamilyNote } from '../services/api';
import { triggerHaptic } from '../services/purrSynthesizer';
import { ContextBreadcrumb } from '../components/ContextBreadcrumb';
import { FileText, Heart, Send, CheckCircle, RefreshCw, MessageSquare } from 'lucide-react';

export const FamilyDeskNoteView: React.FC = () => {
  const { profile } = useApp();
  const [notes, setNotes] = useState<FamilyNote[]>([]);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [noteMessage, setNoteMessage] = useState('');
  const defaultSender = profile === 'trey' ? 'Trey' : profile === 'valen' ? 'Valen' : 'Mom';
  const [noteSender, setNoteSender] = useState(defaultSender);
  const [noteStatus, setNoteStatus] = useState<string | null>(null);
  const [sendingNote, setSendingNote] = useState(false);

  const loadNotes = async () => {
    setLoadingNotes(true);
    try {
      const fetched = await fetchFamilyNotes();
      setNotes(fetched);
    } catch {
      // fail gracefully
    } finally {
      setLoadingNotes(false);
    }
  };

  useEffect(() => {
    loadNotes();
  }, []);

  const handleSendNote = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!noteMessage.trim() || sendingNote) return;

    setSendingNote(true);
    triggerHaptic(30);

    try {
      const res = await postFamilyNote(noteMessage.trim(), noteSender);
      if (res.ok) {
        setNoteStatus(`Saved! Echo will see your note on his desk.`);
        setNoteMessage('');
        await loadNotes();
      } else {
        setNoteStatus(`Could not save note: ${res.message}`);
      }
    } catch (e: any) {
      setNoteStatus(`Could not save note: ${e.message}`);
    } finally {
      setSendingNote(false);
      setTimeout(() => setNoteStatus(null), 6000);
    }
  };

  return (
    <div className="flex-1 overflow-y-auto px-3.5 py-3 space-y-4">
      <ContextBreadcrumb realmName="Family Realm" deckTitle="Desk Notes" realmEmoji="📝" />
      {/* Hero Header */}
      <div className="rounded-2xl p-4 bg-gradient-to-br from-purple-950/40 via-slate-900/80 to-slate-950 border border-purple-500/30 shadow-xl">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-xl bg-purple-500/20 border border-purple-400/40 flex items-center justify-center text-purple-300">
              <FileText className="w-5 h-5" />
            </div>
            <div>
              <h2 className="font-cinzel text-sm font-bold text-purple-300 tracking-wider">
                ECHO'S DESK WHITEBOARD
              </h2>
              <p className="text-[11px] text-slate-300">
                Shared Household Messages & Notes
              </p>
            </div>
          </div>
          <Heart className="w-5 h-5 text-rose-400 animate-pulse" />
        </div>

        <p className="text-xs text-slate-300 leading-relaxed mt-1">
          Have a question, grocery request, or sweet message? Drop a note directly onto Echo's server desk!
        </p>

        {noteStatus && (
          <div className="mt-3 p-2.5 rounded-xl bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 text-xs flex items-center gap-2">
            <CheckCircle className="w-4 h-4 shrink-0 text-emerald-400" />
            <span>{noteStatus}</span>
          </div>
        )}

        {/* Leave Note Form */}
        <form onSubmit={handleSendNote} className="space-y-2 pt-3">
          <div className="flex gap-2">
            <div className="flex bg-black/40 border border-white/15 rounded-xl p-1 shrink-0">
              <button
                type="button"
                onClick={() => setNoteSender('Mom')}
                className={`px-2.5 py-1 text-xs rounded-lg font-medium transition-all ${
                  noteSender === 'Mom'
                    ? 'bg-purple-500/30 text-purple-200 border border-purple-400/40'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                Mom
              </button>
              <button
                type="button"
                onClick={() => setNoteSender('Trey')}
                className={`px-2.5 py-1 text-xs rounded-lg font-medium transition-all ${
                  noteSender === 'Trey'
                    ? 'bg-purple-500/30 text-purple-200 border border-purple-400/40'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                Trey
              </button>
              <button
                type="button"
                onClick={() => setNoteSender('Valen')}
                className={`px-2.5 py-1 text-xs rounded-lg font-medium transition-all ${
                  noteSender === 'Valen'
                    ? 'bg-purple-500/30 text-purple-200 border border-purple-400/40'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                Valen
              </button>
            </div>

            <input
              type="text"
              value={noteSender}
              onChange={(e) => setNoteSender(e.target.value)}
              placeholder="Name"
              className="w-20 bg-black/40 border border-white/15 rounded-xl px-2.5 py-1 text-xs text-white focus:outline-none focus:border-purple-400"
            />
          </div>

          <textarea
            rows={2}
            value={noteMessage}
            onChange={(e) => setNoteMessage(e.target.value)}
            placeholder="Write your note for Echo..."
            className="w-full bg-black/40 border border-white/15 rounded-xl px-3 py-2 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-purple-400 resize-none"
          />

          <button
            type="submit"
            disabled={!noteMessage.trim() || sendingNote}
            className="w-full py-2.5 rounded-xl bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-400 hover:to-indigo-500 text-white font-bold text-xs flex items-center justify-center gap-1.5 shadow-quantum-glow transition-all disabled:opacity-40"
          >
            <Send className="w-3.5 h-3.5" />
            <span>{sendingNote ? 'Saving to Desk...' : 'Leave Note for Echo'}</span>
          </button>
        </form>
      </div>

      {/* Existing Notes Feed */}
      <div className="glass-panel rounded-2xl p-3.5 border border-white/10 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-xs text-white flex items-center gap-1.5">
            <MessageSquare className="w-4 h-4 text-purple-400" /> Recent Whiteboard Notes
          </h3>
          <button
            onClick={loadNotes}
            className="text-[10px] text-purple-300 hover:text-purple-200 flex items-center gap-1 bg-white/5 px-2 py-1 rounded-lg border border-white/10 transition-all"
          >
            <RefreshCw className={`w-3 h-3 ${loadingNotes ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>

        {notes.length === 0 ? (
          <p className="text-xs text-slate-400 text-center py-4">No notes posted yet. Be the first!</p>
        ) : (
          <div className="space-y-2.5">
            {notes.map((n) => (
              <div
                key={n.id}
                className="bg-black/40 rounded-xl p-3 border border-white/10 space-y-1.5 hover:border-purple-500/30 transition-all"
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-purple-300 flex items-center gap-1">
                    <Heart className="w-3 h-3 text-rose-400" /> {n.sender}
                  </span>
                  <span className="text-[10px] font-mono text-slate-400">{n.timestamp}</span>
                </div>
                <p className="text-xs text-slate-200 whitespace-pre-wrap leading-relaxed">{n.text}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
